import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { MessagingIntegrationOption } from "@/lib/member-accessible-integrations";

interface IntegrationChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  integrations: MessagingIntegrationOption[];
  loading?: boolean;
  onConfirm: (integrationId: string) => void | Promise<void>;
}

export function IntegrationChoiceDialog({
  open,
  onOpenChange,
  title = "Escolher conexão",
  description = "Selecione por qual número deseja iniciar a conversa.",
  confirmLabel = "Iniciar conversa",
  integrations,
  loading = false,
  onConfirm,
}: IntegrationChoiceDialogProps) {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedIntegrationId(null);
      return;
    }

    if (integrations.length === 1) {
      setSelectedIntegrationId(integrations[0].id);
      return;
    }

    setSelectedIntegrationId((current) =>
      current && integrations.some((integration) => integration.id === current)
        ? current
        : integrations[0]?.id ?? null,
    );
  }, [open, integrations]);

  const handleConfirm = async () => {
    if (!selectedIntegrationId) return;
    await onConfirm(selectedIntegrationId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Enviar pela conexão</Label>
          <Select
            value={selectedIntegrationId ?? undefined}
            onValueChange={setSelectedIntegrationId}
            disabled={loading || integrations.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a conexão" />
            </SelectTrigger>
            <SelectContent>
              {integrations.map((integration) => (
                <SelectItem key={integration.id} value={integration.id}>
                  {integration.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={loading || !selectedIntegrationId}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
